/**
 * Named lists (#337) talking to the server: turning items into tasks, and
 * bringing open checklist items forward from the previous meeting. Every
 * document change goes through the open editor, so autosave and undo cover it.
 */
import type { Editor } from '@tiptap/core';
import type { JSONContent } from '@tiptap/react';
import type { Node as PmNode } from '@tiptap/pm/model';
import { apiGet, apiPatch, apiPost, CONTENT_SAVED_EVENT } from './api';
import type { SourceType } from './excerpts';
import { findItem, findList, listsIn, type ListKind } from '../components/editor/lists/NamedListNodes';

export interface CarryList { name: string; kind: ListKind; items: JSONContent[] }
interface ListSource { type: SourceType; id: string }

const MAX_TITLE = 500;
const SAVE_WAIT_MS = 30_000;

/** Items with a Turn-into-task request in flight, so a double click makes one task. */
const pending = new Set<string>();

const flagIdsOf = (node: PmNode | null | undefined): string[] =>
  (Array.isArray(node?.attrs.flags) ? (node!.attrs.flags as Array<{ id: string }>).map((f) => f.id) : []);

/** The line an item was filed from, outside any list. */
function sourceLine(doc: PmNode, lineId: string): PmNode | null {
  let hit: PmNode | null = null;
  doc.descendants((n, _pos, parent) => {
    if (hit) return false;
    if (n.attrs.lineId === lineId && parent?.type.name !== 'namedListItem') { hit = n; return false; }
    return true;
  });
  return hit;
}

/** Write `taskId` on this item, and on every item filed from the same line. */
function linkTask(editor: Editor, itemId: string, ref: string | null, taskId: string): void {
  const tr = editor.state.tr;
  editor.state.doc.descendants((n, pos) => {
    if (n.type.name !== 'namedListItem') return true;
    if (n.attrs.itemId === itemId || (ref && n.attrs.ref === ref)) tr.setNodeMarkup(pos, undefined, { ...n.attrs, taskId });
    return true;
  });
  if (tr.docChanged) editor.view.dispatch(tr);
}

/**
 * Make a task from a list item. A task belongs to the LINE: an item filed
 * from a line another item already made a task from reuses that task.
 * Returns the task id, or null when there was nothing to do.
 */
export async function turnItemIntoTask(editor: Editor, itemId: string, source: ListSource): Promise<string | null> {
  if (pending.has(itemId)) return null;
  pending.add(itemId);
  try { return await makeTask(editor, itemId, source); } finally { pending.delete(itemId); }
}

async function makeTask(editor: Editor, itemId: string, source: ListSource): Promise<string | null> {
  const hit = findItem(editor.state.doc, itemId);
  if (!hit || hit.node.attrs.taskId) return null;
  const ref = hit.node.attrs.ref as string | null;
  if (ref) {
    let existing: string | null = null;
    editor.state.doc.descendants((n) => {
      if (!existing && n.type.name === 'namedListItem' && n.attrs.ref === ref && n.attrs.taskId) existing = n.attrs.taskId;
      return !existing;
    });
    if (existing) { linkTask(editor, itemId, ref, existing); return existing; }
  }
  const title = (hit.node.firstChild?.textContent ?? '').trim().slice(0, MAX_TITLE);
  if (!title) return null;
  const flagIds = ref ? flagIdsOf(sourceLine(editor.state.doc, ref)) : flagIdsOf(hit.node.firstChild);
  const { task } = await apiPost<{ task: { id: string } }>('/list-items/task', {
    source_type: source.type, source_id: source.id, title, flag_ids: flagIds,
  });
  if (!editor.isDestroyed) linkTask(editor, itemId, ref, task.id);
  return task.id;
}

/** Tasks for every open top-level item without one, one at a time. Returns how many were made. */
export async function turnOpenItemsIntoTasks(editor: Editor, listId: string, source: ListSource): Promise<number> {
  const hit = findList(editor.state.doc, listId);
  if (!hit) return 0;
  const ids: string[] = [];
  hit.node.forEach((c) => { if (!c.attrs.checked && !c.attrs.taskId) ids.push(c.attrs.itemId); });
  let made = 0;
  for (const id of ids) if (await turnItemIntoTask(editor, id, source)) made++;
  return made;
}

/**
 * Add carried items to the same-named lists (case-insensitive), making any
 * list that is missing at the end of the document. Items already carried here
 * (by `carriedFrom`) and anything that is not a valid list item are skipped.
 * One transaction, so one undo takes it back. Returns how many were added.
 */
export function insertCarried(editor: Editor, lists: CarryList[]): number {
  const { schema } = editor.state;
  const already = new Set<string>();
  editor.state.doc.descendants((n) => {
    if (n.type.name === 'namedListItem' && n.attrs.carriedFrom) already.add(n.attrs.carriedFrom);
    return true;
  });
  const tr = editor.state.tr;
  let added = 0;
  for (const carry of lists) {
    const nodes: PmNode[] = [];
    for (const json of carry.items ?? []) {
      if (!json || json.type !== 'namedListItem') continue;
      const from = json.attrs?.carriedFrom as string | undefined;
      if (from && already.has(from)) continue;
      try {
        const node = schema.nodeFromJSON(json);
        node.check();
        nodes.push(node);
        if (from) already.add(from);
      } catch { /* malformed: skip it */ }
    }
    if (!nodes.length) continue;
    const name = (carry.name ?? '').toLowerCase();
    const target = listsIn(tr.doc).find((l) => l.name.toLowerCase() === name);
    if (target) {
      const list = tr.doc.nodeAt(target.pos)!;
      tr.insert(target.pos + list.nodeSize - 1, nodes);
    } else {
      tr.insert(tr.doc.content.size, schema.nodes.namedList.create(
        { listId: crypto.randomUUID(), name: carry.name || 'List', kind: carry.kind === 'plain' ? 'plain' : 'checklist' }, nodes));
    }
    added += nodes.length;
  }
  if (added) editor.view.dispatch(tr);
  return added;
}

/** Fetch what the previous meeting left open: `due` says whether the automatic pass should run now. */
export function fetchCarry(instanceId: string, today: string): Promise<{ due: boolean; lists: CarryList[] }> {
  return apiGet(`/meeting-instances/${instanceId}/list-carry?today=${encodeURIComponent(today)}`);
}

/** Resolves true when this document's body is next saved, false after `ms`. */
function nextSave(path: string, ms: number): Promise<boolean> {
  return new Promise((resolve) => {
    const onSaved = (e: Event) => {
      if ((e as CustomEvent<{ path: string }>).detail?.path !== path) return;
      done(true);
    };
    const timer = setTimeout(() => done(false), ms);
    function done(saved: boolean) {
      clearTimeout(timer);
      window.removeEventListener(CONTENT_SAVED_EVENT, onSaved);
      resolve(saved);
    }
    window.addEventListener(CONTENT_SAVED_EVENT, onSaved);
  });
}

/**
 * The automatic pass when a meeting opens: only once its date has come
 * (Michael creates meetings ahead of time for prep notes), and only once -
 * the meeting is marked even when there was nothing to bring, so an item
 * deleted after carrying never comes back. The mark waits for the save that
 * holds the carried items (review #6): if that save never happens the next
 * open tries again, and `carriedFrom` stops anything arriving twice. An
 * editor that has gone (the user moved to another meeting) gets nothing (#5).
 */
export async function carryOnOpen(editor: Editor, instanceId: string, today: string, opts: { saveTimeoutMs?: number } = {}): Promise<number> {
  const { due, lists } = await fetchCarry(instanceId, today);
  if (!due || editor.isDestroyed) return 0;
  const path = `/meeting-instances/${instanceId}`;
  const saved = nextSave(path, opts.saveTimeoutMs ?? SAVE_WAIT_MS);
  const added = insertCarried(editor, lists);
  if (added && !(await saved)) return added;
  await apiPatch(path, { lists_carried_at: new Date().toISOString() });
  return added;
}

/** Today in the browser's time zone, as YYYY-MM-DD. */
export function localToday(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
